// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';
import { addClassToHotTable, removeClassFromHotTable } from '../utils/domUtils/hotTableDomMutationUtils';
import {
    fixSelectionBorderDisplay,
    repositionAutofillHandle,
} from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import { getAndClearTransactionIdForNextUpdateCellSelection } from '../utils/hotInstanceUtils/selection/_hotTableSelectionTransactionUtils';
import useRefCallback from '../../../utils/react/useRefCallback';
import { TABLE_GRID_EDITOR_KEY, TABLE_TITLE_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { finishEditingElement, startEditingElement } from '../../selection/selectionActions';
import { updateCellSelectionMeta } from '../tableActions';

/**
 * Custom hook for handling table cell selection operations
 *
 * @param {Object} props - Hook configuration
 * @returns {Object} Handler functions for table selection
 */
export const useTableSelectionHandlers = ({
    elementId,
    currentlyEditingEditorId,
    currentEditorKey,

    hotTableInstanceRef,
    hotTableContainerRef,
}) => {
    const dispatch = useDispatch();

    const dispatchStartEditingTableGrid = useCallback(
        () =>
            dispatch(
                startEditingElement({
                    id: elementId,
                    editorKey: TABLE_GRID_EDITOR_KEY,
                    editorId: getTableGridEditorId(elementId),
                    elementId: undefined,
                }),
            ),
        [elementId],
    );

    const dispatchSetCellSelection = useCallback(
        (cellSelection, selectionLayerLevel, transactionId) =>
            dispatch(updateCellSelectionMeta(elementId, cellSelection, selectionLayerLevel, transactionId)),
        [elementId],
    );

    const dispatchFinishEditingElement = useCallback(() => dispatch(finishEditingElement(elementId)), [elementId]);

    const afterSelection = useRefCallback(
        (row, column, row2, column2, _, selectionLayerLevel) => {
            requestAnimationFrame(() => {
                // Applied using plain javascript to improve performance, to avoid triggering another render
                addClassToHotTable(hotTableInstanceRef.current, 'is-selecting-cell');
            });

            fixSelectionBorderDisplay(hotTableInstanceRef.current);

            const isEditingCellOrGrid =
                !currentlyEditingEditorId ||
                currentlyEditingEditorId === getTableGridEditorId(elementId) ||
                currentlyEditingEditorId === getTableCellEditorId(elementId);

            if (!isEditingCellOrGrid) {
                requestAnimationFrame(() => dispatchFinishEditingElement());
            }

            // Apply new cell selection to redux state if it has changed
            const newCellSelection = [row, column, row2, column2];

            repositionAutofillHandle(hotTableInstanceRef.current, hotTableContainerRef);

            const transactionId = getAndClearTransactionIdForNextUpdateCellSelection(hotTableInstanceRef.current);

            dispatchSetCellSelection(newCellSelection, selectionLayerLevel, transactionId);
        },
        [
            elementId,
            currentlyEditingEditorId,
            hotTableInstanceRef,
            hotTableContainerRef,
            dispatchSetCellSelection,
            dispatchFinishEditingElement,
        ],
    );

    const afterSelectionEnd = useRefCallback(() => {
        requestAnimationFrame(() => {
            // Applied using plain javascript to improve performance, to avoid triggering another render
            removeClassFromHotTable(hotTableInstanceRef.current, 'is-selecting-cell');
        });

        if (currentlyEditingEditorId !== getTableGridEditorId(elementId)) {
            dispatchStartEditingTableGrid();
        }
    }, [elementId, currentlyEditingEditorId, dispatchStartEditingTableGrid]);

    const afterDeselect = useRefCallback(() => {
        // Use setTimeout here to make sure all operations have run before we check if
        // there are still selection on the table.
        setTimeout(() => {
            if (hotTableInstanceRef.current?.getSelected()) return;

            // Don't deselect / finish editing if the deselection occurred because the table title editor is focused
            if (currentEditorKey !== TABLE_TITLE_EDITOR_KEY) dispatchFinishEditingElement();

            dispatchSetCellSelection(null);
        });
    }, [elementId, currentEditorKey, hotTableInstanceRef, dispatchFinishEditingElement, dispatchSetCellSelection]);

    return {
        afterSelection,
        afterSelectionEnd,
        afterDeselect,
    };
};

export default useTableSelectionHandlers;
